import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Buildings } from "@phosphor-icons/react";
import { ITEM_STATUS, OVERALL, describeExpiry } from "./status";

const Section = ({ children }) => (
  <section data-testid="client-readiness" className="bg-white border-2 border-slate-200 rounded-xl p-5 sm:p-6">
    <div className="flex items-center gap-2.5 mb-4">
      <Buildings size={22} weight="bold" className="text-slate-700" />
      <h2 className="font-display text-lg font-black tracking-tight text-slate-900">Client Readiness</h2>
    </div>
    {children}
  </section>
);

export default function ClientReadiness({ profileId }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/config/clients").then((r) => setClients((r.data || []).filter((c) => c.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!clientId || !profileId) { setData(null); return; }
    setLoading(true);
    api.get(`/training/readiness?profile_id=${profileId}&client_id=${clientId}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [clientId, profileId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Section>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select data-testid="readiness-client" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Choose a client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {data && (
          <span data-testid="readiness-overall" className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${OVERALL[data.overall].cls}`}>
            {OVERALL[data.overall].label}
          </span>
        )}
      </div>
      {!clientId && <p className="text-slate-400 text-sm">Select a client to check readiness against their training requirements.</p>}
      {loading && <p className="text-slate-400 text-sm">Checking…</p>}
      {data && !loading && (
        data.items.length === 0
          ? <p className="text-slate-400 text-sm">This client has no training requirements set yet.</p>
          : <div className="flex flex-col gap-2">
              {data.items.map((it) => {
                const s = ITEM_STATUS[it.status] || ITEM_STATUS.missing;
                return (
                  <div key={it.requirement_id} data-testid={`readiness-item-${it.requirement_id}`}
                       className={`flex items-center justify-between gap-3 border-2 rounded-lg px-3 py-2.5 ${s.cls}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {it.label}
                        {it.level === "preferred" && <span className="ml-2 text-[10px] font-bold uppercase text-slate-500">preferred</span>}
                      </div>
                      <div className="text-xs text-slate-600">
                        {describeExpiry(it)}
                        {it.min_validity_days ? ` · needs ≥ ${it.min_validity_days} days` : ""}
                        {it.client_wording ? ` · ${it.client_wording}` : ""}
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-white/70">{s.label}</span>
                  </div>
                );
              })}
            </div>
      )}
    </Section>
  );
}
